import {gql} from '@apollo/client';
import {groupByUniqueKey, compact} from '@benchling/js-utils';
import {DocumentNode} from 'graphql';
import React from 'react';

import {assert} from 'Asserts';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {assertIsAccessible, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {
  FetchSelectorsByIdQuery,
  FetchSelectorsByIdQueryVariables,
} from 'notebook/structured-tables/hooks/__generated/FetchSelectorsByIdQuery';
import {
  FETCH_SELECTORS_BY_ID_QUERY,
  getSelectorsById,
  SchemaFieldSelectorsDict,
} from 'notebook/structured-tables/hooks/useSelectorsById';
import {Schema} from 'schemas/SchemaConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';
import StepNav, {StepItemStatus, StepNavVariant} from 'ui-library/StepNav';
import {assertNever} from 'util/Types';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {FixFailedRoutingAndMappingAllSchemasQuery} from './__generated/FixFailedRoutingAndMappingAllSchemasQuery';
import {
  FixFailedRoutingAndMappingModalQuery,
  FixFailedRoutingAndMappingModalQueryVariables,
} from './__generated/FixFailedRoutingAndMappingModalQuery';
import {
  ProcessTask,
  ProcessAsyncRecord,
  isAsyncRecordRoutingRecord,
  FixFailedRoutingAndMappingStepKey,
} from './FixFailedRoutingAndMappingConstants';
import {getSchemaMappers} from './FixFailedRoutingAndMappingUtils';
import PerformManualMappingPane from './PerformManualMappingPane';
import SelectDownstreamNodesPane from './SelectDownstreamNodesPane';
import SelectErrorPane from './SelectErrorPane';
import {useFixFailedRoutingAndMappingState} from './useFixFailedRoutingAndMappingState';

const SCHEMA_MAPPER_FRAGMENT: DocumentNode = gql`
  fragment FixFailedRoutingAndMappingSchemaMapper on ProcessFlowchartSchemaMapperNode {
    id
    toNodeConfig {
      id
    }
    fromSchemas {
      ... on ProcessTaskSchemaNode {
        id
        name
      }
      ... on ProcessOutputSchemaNode {
        id
        name
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
    toSchema {
      ... on ProcessTaskSchemaNode {
        id
        name
      }
      ... on ProcessOutputSchemaNode {
        id
        name
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
`;

export const FIX_FAILED_ROUTING_AND_MAPPING_MODAL_QUERY = gql`
  query FixFailedRoutingAndMappingModalQuery($processTaskId: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTask(id: $processTaskId) {
      ... on ProcessTaskNode {
        id
        humanId
        rootTask {
          id
          process {
            id
          }
          taskSchema {
            ... on ProcessTaskSchemaNode {
              id
            }
            ... on InaccessibleInfoNode {
              ...InaccessibleInfoGraphQL
            }
          }
        }
        process {
          id
        }
        fields
        outputs {
          id
          humanId
          fields
          ...HasArchiveRecordGraphQL
        }
        taskSchema {
          ... on ProcessTaskSchemaNode {
            id
            name
            fields
            allFields
            clientOrigin
            outputSchema {
              id
              name
              fields
              allFields
            }
            statusLifecycle {
              id
              schemaExecutionType
            }
          }
          ... on InaccessibleInfoNode {
            ...InaccessibleInfoGraphQL
          }
        }
        processAsyncRecords {
          id
          status
          details
          asyncData {
            ... on ProcessStartAutomatedMappingRecordNode {
              id
              blockedEndNodesAndExpressions {
                blockedEndNode {
                  ... on ProcessEndNodeDetailsNode {
                    ...ProcessEndNodeDetailsGraphQL
                  }
                }
                blockingExpression {
                  ... on BenchlingExpressionNode {
                    expressionJson
                  }
                  ... on InaccessibleInfoNode {
                    ...InaccessibleInfoGraphQL
                  }
                }
                fromSchemas {
                  ... on ProcessTaskSchemaNode {
                    id
                    name
                  }
                  ... on ProcessOutputSchemaNode {
                    id
                    name
                  }
                  ... on InaccessibleInfoNode {
                    ...InaccessibleInfoGraphQL
                  }
                }
              }
              blockedMappersAndExpressions {
                schemaMapper {
                  ... on ProcessFlowchartSchemaMapperNode {
                    ...FixFailedRoutingAndMappingSchemaMapper
                  }
                }
                blockingExpressions {
                  ... on BenchlingExpressionNode {
                    expressionJson
                  }
                  ... on InaccessibleInfoNode {
                    ...InaccessibleInfoGraphQL
                  }
                }
                blockingRouters {
                  ... on ProcessRouterNodeDetailsNode {
                    id
                  }
                }
              }
            }
            ... on ProcessFlowchartSchemaMappingRecordNode {
              id
              schemaMapper {
                ... on ProcessFlowchartSchemaMapperNode {
                  ...FixFailedRoutingAndMappingSchemaMapper
                }
              }
            }
            ... on ProcessFlowchartExecuteSchemaMapperRecordNode {
              id
              schemaMapper {
                ... on ProcessFlowchartSchemaMapperNode {
                  ...FixFailedRoutingAndMappingSchemaMapper
                }
              }
            }
          }
        }
        ...StudyableTypedValueDataGraphQL
      }
      ...HasArchiveRecordGraphQL
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('StudyableTypedValueData').fragment}
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('ProcessEndNodeDetails').fragment}
  ${SCHEMA_MAPPER_FRAGMENT}
`;

export const GET_ALL_SCHEMAS_QUERY = gql`
  query FixFailedRoutingAndMappingAllSchemasQuery @skipAudit @engComponent(name: WORKFLOWS) {
    ...AllStorableSchemasGraphQL
    ...AllTagSchemasGraphQL
    ...AllResultSchemasGraphQL
    ...AllProcessTaskSchemasForSchemaConfigGraphQL
  }
  ${getFragmentWrapper('AllStorableSchemas').fragment}
  ${getFragmentWrapper('AllTagSchemas').fragment}
  ${getFragmentWrapper('AllResultSchemas').fragment}
  ${getFragmentWrapper('AllProcessTaskSchemasForSchemaConfig').fragment}
`;

// Re-export for backwards compatibility with existing consumers.
export {FixFailedRoutingAndMappingStepKey} from './FixFailedRoutingAndMappingConstants';

const SELECT_ERRORS_PANE = {
  key: FixFailedRoutingAndMappingStepKey.SELECT_ERRORS,
  title: 'Select error(s)',
};
const SELECT_DOWNSTREAM_NODES_PANE = {
  key: FixFailedRoutingAndMappingStepKey.SELECT_DOWNSTREAM_NODES,
  title: 'Select next step(s) in workflow',
};
const PERFORM_MANUAL_MAPPING_PANE = {
  key: FixFailedRoutingAndMappingStepKey.TABLE_REVIEW,
  title: 'Review',
};

const DEFAULT_STATUS_BY_STEP = {
  [FixFailedRoutingAndMappingStepKey.SELECT_ERRORS]: StepItemStatus.UNVISITED,
  [FixFailedRoutingAndMappingStepKey.SELECT_DOWNSTREAM_NODES]: StepItemStatus.UNVISITED,
  [FixFailedRoutingAndMappingStepKey.TABLE_REVIEW]: StepItemStatus.UNVISITED,
};

function getFixFailedRoutingAndMappingModalSteps(
  selectedRecord: ProcessAsyncRecord | null
): Array<{key: FixFailedRoutingAndMappingStepKey; title: string}> {
  if (selectedRecord != null && isAsyncRecordRoutingRecord(selectedRecord)) {
    return [SELECT_ERRORS_PANE, SELECT_DOWNSTREAM_NODES_PANE, PERFORM_MANUAL_MAPPING_PANE];
  }
  return [SELECT_ERRORS_PANE, PERFORM_MANUAL_MAPPING_PANE];
}

interface FixFailedRoutingAndMappingModalWithData {
  isLoading: false;
  selectorsById: SchemaFieldSelectorsDict;
  selectedItem: ProcessTask;
  onRequestClose: () => void;
  allSchemasById: SchemaById;
}

interface FixFailedRoutingAndMappingModalStillLoading {
  isLoading: true;
  selectorsById?: null;
  selectedItem?: null;
  onRequestClose: () => void;
  allSchemasById?: null;
}

type FixFailedRoutingAndMappingModalProps =
  | FixFailedRoutingAndMappingModalWithData
  | FixFailedRoutingAndMappingModalStillLoading;

interface QueryingFixFailedMappingMappingModalProps {
  selectedId: string;
  onRequestClose: () => void;
}

export function FixFailedRoutingAndMappingModal(props: FixFailedRoutingAndMappingModalProps): JSX.Element {
  const {
    currentPane,
    selectedRecord,
    itemsToMap,
    itemIdsToMapperId,
    itemIdsToEndNodeId,
    fromItemIdsToTables,
    mapperIdsToDownstreamSchemaName,
    setItemIdsToMapperIds,
    setItemIdsToEndNodeIds,
    setFromItemIdsToTables,
    handleSelectedRecord,
    getModalButtons,
  } = useFixFailedRoutingAndMappingState({
    selectedItem: props.selectedItem ?? null,
    selectorsById: props.selectorsById ?? null,
    onRequestClose: props.onRequestClose,
  });

  const getModalBody = (): JSX.Element | null => {
    if (props.isLoading) {
      return <CenteredHorizontalThrobber />;
    }
    switch (currentPane) {
      case FixFailedRoutingAndMappingStepKey.SELECT_ERRORS:
        return (
          <SelectErrorPane
            asyncRecords={props.selectedItem.processAsyncRecords}
            onSelectError={handleSelectedRecord}
          />
        );
      case FixFailedRoutingAndMappingStepKey.SELECT_DOWNSTREAM_NODES:
        assert(
          itemsToMap != null && selectedRecord != null && mapperIdsToDownstreamSchemaName != null,
          'Expected record to be selected and mappable items to be defined'
        );
        assert(isAsyncRecordRoutingRecord(selectedRecord), 'Expected selected record to be a routing error');
        return (
          <SelectDownstreamNodesPane
            itemIdsToSelectedMapperIds={itemIdsToMapperId}
            itemIdsToSelectedEndNodeIds={itemIdsToEndNodeId}
            itemsToMap={itemsToMap}
            mapperIdsToDownstreamSchemaName={mapperIdsToDownstreamSchemaName}
            asyncRecord={selectedRecord}
            selectorsById={props.selectorsById}
            task={props.selectedItem}
            onChangeItemIdsToSelectedMapperIds={setItemIdsToMapperIds}
            onChangeItemIdsToSelectedEndNodeIds={setItemIdsToEndNodeIds}
            allSchemasById={props.allSchemasById}
          />
        );
      case FixFailedRoutingAndMappingStepKey.TABLE_REVIEW: {
        assert(
          mapperIdsToDownstreamSchemaName != null && itemsToMap != null,
          'Expected flowchart data to be defined'
        );
        return (
          <PerformManualMappingPane
            fromItems={itemsToMap}
            mapperIdsToDownstreamSchemaName={mapperIdsToDownstreamSchemaName}
            fromItemIdsToTables={fromItemIdsToTables}
            onChangeFromItemIdsToTables={setFromItemIdsToTables}
          />
        );
      }
      default:
        assert(false, `Unexpected type ${assertNever(currentPane)}`);
        return null;
    }
  };

  return (
    <ExpandableModal
      title="Fix failed mapping errors"
      onRequestClose={props.onRequestClose}
      buttons={getModalButtons()}
      expandHeight={true}
      large={true}
      modalKey="fixFailedMapping"
      rememberExpandedSetting={true}
      wrapInForm={false}
    >
      <StepNav
        steps={getFixFailedRoutingAndMappingModalSteps(selectedRecord)}
        statusByStep={DEFAULT_STATUS_BY_STEP}
        currentStepKey={currentPane}
        onUpdateCurrentStep={() => {}}
        variant={StepNavVariant.FLAT}
      />
      {getModalBody()}
    </ExpandableModal>
  );
}

function getInaccessibleItems(
  queryData: FixFailedRoutingAndMappingModalQuery
): Array<InaccessibleInfoGraphQL> {
  const inaccessibleItems: Array<InaccessibleInfoGraphQL> = [];

  const task = queryData.processTask;
  if (isInaccessible(task)) {
    return [task];
  }

  if (isInaccessible(task.taskSchema)) {
    inaccessibleItems.push(task.taskSchema);
  }

  for (const asyncRecord of task.processAsyncRecords) {
    const schemaMappers = getSchemaMappers(asyncRecord);
    for (const schemaMapper of schemaMappers) {
      if (isInaccessible(schemaMapper.toSchema)) {
        inaccessibleItems.push(schemaMapper.toSchema);
      }
    }
  }
  return inaccessibleItems;
}

export default function QueryingFixFailedRoutingAndMappingModal(
  props: QueryingFixFailedMappingMappingModalProps
): JSX.Element {
  const allSchemasQueryResult =
    useBenchlingQuery<FixFailedRoutingAndMappingAllSchemasQuery>(GET_ALL_SCHEMAS_QUERY);
  const selectorQueryResult = useBenchlingQuery<FetchSelectorsByIdQuery, FetchSelectorsByIdQueryVariables>(
    FETCH_SELECTORS_BY_ID_QUERY,
    {
      variables: {includeArchived: true},
    }
  );
  const queryResult = useBenchlingQuery<
    FixFailedRoutingAndMappingModalQuery,
    FixFailedRoutingAndMappingModalQueryVariables
  >(FIX_FAILED_ROUTING_AND_MAPPING_MODAL_QUERY, {
    variables: {processTaskId: props.selectedId},
  });
  const selectors =
    selectorQueryResult.type === QueryResultType.SUCCESS ? selectorQueryResult.data.schemaFieldSelectors : [];
  const selectorsById = getSelectorsById(selectors);

  const inaccessibleItems =
    queryResult.type !== QueryResultType.SUCCESS ? [] : getInaccessibleItems(queryResult.data);
  if (inaccessibleItems.length > 0) {
    return (
      <WorkspaceItemInaccessibleGuard
        inaccessibleInfo={inaccessibleItems[0]}
        modelType={ModelTypeConstants.PROCESS_TASK}
      />
    );
  }
  const isLoading =
    selectorQueryResult.type !== QueryResultType.SUCCESS ||
    queryResult.type !== QueryResultType.SUCCESS ||
    allSchemasQueryResult.type !== QueryResultType.SUCCESS;

  return isLoading ? (
    <FixFailedRoutingAndMappingModal isLoading={true} onRequestClose={props.onRequestClose} />
  ) : (
    <FixFailedRoutingAndMappingModal
      isLoading={false}
      selectorsById={selectorsById}
      selectedItem={assertIsAccessible(queryResult.data.processTask)}
      onRequestClose={props.onRequestClose}
      allSchemasById={groupByUniqueKey(
        [
          ...Object.values(allSchemasQueryResult.data).flat(),
          ...compact(
            allSchemasQueryResult.data.processTaskSchemasSupportingPlatformFeature.map(
              ({outputSchema}) => outputSchema
            )
          ),
        ] as Array<Schema>,
        ({id}) => id
      )}
    />
  );
}
